import React, { createContext, useContext, useEffect, useState } from 'react';
import { UnleashClient } from 'unleash-proxy-client';
import envVariables from '../env';
import useAuth from './useAuth';

type FeatureFlagState = {
  flags: {
    'mycambridge.users-page'?: boolean;
    'mycambridge.subjects'?: boolean;
    'mycambridge.profile-photo'?: boolean;
    'mycambridge.resend-invitation'?: boolean;
  };
  haveFlagsLoaded: boolean;
};

const FeatureFlagContext = createContext<FeatureFlagState>({
  flags: {},
  haveFlagsLoaded: false,
});

const useFeatureFlags = (): FeatureFlagState => useContext(FeatureFlagContext);

const unleash = new UnleashClient({
  url: envVariables.FEATURE_FLAG_PROXY_URL,
  clientKey: envVariables.FEATURE_FLAG_CLIENT_KEY,
  refreshInterval: envVariables.FEATURE_FLAG_REFRESH_INTERVAL,
  appName: envVariables.FEATURE_FLAG_APP_NAME,
  context: { properties: { environmentName: envVariables.NODE_ENV } },
});

export const FeatureFlagProvider: React.FC = ({ children }) => {
  const { user } = useAuth();

  const [flags, setFlags] = useState({
    'mycambridge.users-page': unleash.isEnabled('mycambridge.users-page'),
    'mycambridge.subjects': unleash.isEnabled('mycambridge.subjects'),
    'mycambridge.profile-photo': unleash.isEnabled('mycambridge.profile-photo'),
    'mycambridge.resend-invitation': unleash.isEnabled(
      'mycambridge.resend-invitation'
    ),
  });
  const [haveFlagsLoaded, setHaveFlagsLoaded] = useState(false);

  useEffect(() => {
    unleash.start();
    unleash.on('update', () => {
      setFlags((prev) => ({
        ...prev,
        'mycambridge.users-page': unleash.isEnabled('mycambridge.users-page'),
        'mycambridge.subjects': unleash.isEnabled('mycambridge.subjects'),
        'mycambridge.profile-photo': unleash.isEnabled(
          'mycambridge.profile-photo'
        ),
        'mycambridge.resend-invitation': unleash.isEnabled(
          'mycambridge.resend-invitation'
        ),
      }));

      if (!haveFlagsLoaded) {
        setHaveFlagsLoaded(true);
      }

      return () => unleash.stop();
    });
  }, []);

  useEffect(() => {
    if (user?.id) {
      unleash.updateContext({
        userId: user.id,
        properties: { environmentName: envVariables.NODE_ENV },
      });
    }
  }, [user]);

  return (
    <FeatureFlagContext.Provider value={{ flags, haveFlagsLoaded }}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

export default useFeatureFlags;
